/**
 * BonLototoForm.jsx — Bon d'Isolation Énergétique (LOTOTO) — Lafarge
 * Wrapper UI dédié plein écran autour du tableau lignes (mode PCED rattaché ou manuel).
 * Ajoute les blocs LOTOTO :
 *   - En-tête (description tâche, lieu, dates)
 *   - Étapes 1-6 procédure LOTOTO (IPHA, séparation, condamnation, dissipation, VAT, MAT)
 *   - Cadenas individuels par intervenant
 *   - Tests fonctionnels au déblocage
 *   - Signataires (Chargé de consignation, Intervenants, Restitution)
 *
 * Note : Le tableau des sources d'énergie + sélection mode PCED/manuel reste fourni par
 * le composant <BonIESection /> passé en `children`.
 */
import { Lock } from 'lucide-react';

function Checkbox({ checked, onChange, label, testid, className = '' }) {
  return (
    <label className={`flex items-start gap-2 cursor-pointer text-xs ${className}`} data-testid={testid}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 accent-[#DA291C] flex-shrink-0" />
      <span className="leading-snug">{label}</span>
    </label>
  );
}

function SectionTitle({ children, accent = 'bg-[#09090B]' }) {
  return (
    <div className={`${accent} text-white text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-2`}>{children}</div>
  );
}

export default function BonLototoForm({ extra, setExtra, dateDebut, dateFin, titre, lieu, entreprise, children, compact = false }) {
  return (
    <div className="space-y-4 border-2 border-emerald-700 bg-white" data-testid="bon-lototo-form">
      <div className="bg-emerald-700 text-white px-4 py-3 flex items-center gap-3">
        <Lock className="w-6 h-6" strokeWidth={2.5} />
        <div>
          <div className="text-2xl font-display font-black tracking-tighter">BON D'ISOLATION ÉNERGÉTIQUE — LOTOTO</div>
          <div className="text-[10px] uppercase tracking-wider opacity-90">
            Lock Out — Tag Out — Try Out — Procédure de consignation des sources d'énergie
          </div>
        </div>
      </div>

      <div className="px-4 pb-4 space-y-4">
        {/* En-tête — masqué en mode compact (hérités d'AT) */}
        {!compact && (
        <div className="grid sm:grid-cols-2 gap-3 text-xs">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Description tâche</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{titre || '—'}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Lieu</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{lieu || '—'}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Entreprise intervenante</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{entreprise || 'Travaux internes'}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Validité</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{dateDebut || '—'} → {dateFin || '—'}</div>
          </div>
        </div>
        )}

        {/* Lignes (PCED / manuel) — fourni par BonIESection (children) */}
        <div>
          <SectionTitle>Sources d'énergie & dispositifs d'isolement</SectionTitle>
          <div className="border border-stone-200 border-t-0 bg-stone-50/30">
            {children}
          </div>
        </div>

        {/* Étapes 6 — Procédure LOTOTO */}
        <div>
          <SectionTitle accent="bg-[#DA291C]">Procédure LOTOTO — 6 étapes obligatoires</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 text-xs">
            <Checkbox checked={extra.lototo_etape1_ipha} onChange={(v) => setExtra('lototo_etape1_ipha', v)}
              label="Étape 1 — IPHA : Identification, Préparation, Habilitations, Annonce des consignations" testid="bl-et1" />
            <Checkbox checked={extra.lototo_etape2_separation} onChange={(v) => setExtra('lototo_etape2_separation', v)}
              label="Étape 2 — Séparation : sectionnement visible de toutes les sources" testid="bl-et2" />
            <Checkbox checked={extra.lototo_etape3_condamnation} onChange={(v) => setExtra('lototo_etape3_condamnation', v)}
              label="Étape 3 — Condamnation : cadenas + étiquette de consignation sur chaque DI" testid="bl-et3" />
            <Checkbox checked={extra.lototo_etape4_dissipation} onChange={(v) => setExtra('lototo_etape4_dissipation', v)}
              label="Étape 4 — Dissipation des énergies résiduelles (pression, gravitaire, capacité, thermique)" testid="bl-et4" />
            <Checkbox checked={extra.lototo_etape5_vat} onChange={(v) => setExtra('lototo_etape5_vat', v)}
              label="Étape 5 — Vérification d'absence de tension (VAT) au plus près de la zone de travail" testid="bl-et5" />
            <Checkbox checked={extra.lototo_etape6_mat} onChange={(v) => setExtra('lototo_etape6_mat', v)}
              label="Étape 6 — Mise à la terre et en court-circuit (MAT) si HT (recommandé BT)" testid="bl-et6" />
          </div>
        </div>

        {/* Cadenas individuels + Tests */}
        <div>
          <SectionTitle>Cadenas individuels & Tests fonctionnels</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 text-xs bg-stone-50/50">
            <Checkbox checked={extra.cadenas_individuel} onChange={(v) => setExtra('cadenas_individuel', v)}
              label="Chaque intervenant a posé son cadenas individuel sur le boîtier de consignation collectif" testid="bl-cadenas-ind" />
            <Checkbox checked={extra.test_out_tente} onChange={(v) => setExtra('test_out_tente', v)}
              label="TRY-OUT effectué : tentative de mise en marche pour vérifier que l'isolement est effectif" testid="bl-test-out" />
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Couleur(s) cadenas posés</div>
              <input type="text" value={extra.couleurs_cadenas || ''} onChange={(e) => setExtra('couleurs_cadenas', e.target.value)}
                data-testid="bl-couleurs" className="w-full px-2 py-1 border border-stone-300"
                placeholder="Rouge (chargé de consignation), Jaune (chargé de travaux), Vert (intervenants)…" />
            </div>
          </div>
        </div>

        {/* Signataires */}
        <div>
          <SectionTitle accent="bg-emerald-700">Signataires & Restitution</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 grid sm:grid-cols-2 gap-3 text-xs bg-emerald-50/30">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Chargé de consignation (Lafarge)</div>
              <input type="text" value={extra.lototo_charge_consignation || ''} onChange={(e) => setExtra('lototo_charge_consignation', e.target.value)}
                data-testid="bl-charge-cons" className="w-full px-2 py-1 border border-stone-300" placeholder="Nom + habilitation" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Chargé de travaux (EE ou interne)</div>
              <input type="text" value={extra.lototo_charge_travaux || ''} onChange={(e) => setExtra('lototo_charge_travaux', e.target.value)}
                data-testid="bl-charge-trav" className="w-full px-2 py-1 border border-stone-300" placeholder="Nom + habilitation" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Date / heure de consignation</div>
              <input type="datetime-local" value={extra.lototo_dt_consignation || ''} onChange={(e) => setExtra('lototo_dt_consignation', e.target.value)}
                data-testid="bl-dt-cons" className="w-full px-2 py-1 border border-stone-300" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Date / heure de restitution</div>
              <input type="datetime-local" value={extra.lototo_dt_restitution || ''} onChange={(e) => setExtra('lototo_dt_restitution', e.target.value)}
                data-testid="bl-dt-rest" className="w-full px-2 py-1 border border-stone-300" />
            </div>
            <div className="sm:col-span-2 pt-2 border-t border-emerald-200">
              <Checkbox checked={extra.zone_evacuee} onChange={(v) => setExtra('zone_evacuee', v)}
                label="Zone évacuée, personnel et matériel retirés avant remise sous énergie" testid="bl-zone-evac" />
              <Checkbox checked={extra.cadenas_retires} onChange={(v) => setExtra('cadenas_retires', v)}
                label="Tous les cadenas individuels ont été retirés avant la déconsignation" testid="bl-cad-ret" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
